import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ClipboardList, LineChart, MessageCircle, Laptop, Users, Dumbbell, BookOpen, Smartphone } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { CartDrawer } from "@/components/CartDrawer";
import { Footer } from "@/components/Footer";
import { useSiteSettings } from "@/lib/settings";
import { Cite } from "@/components/Cite";
import { Guarantee } from "@/components/Guarantee";
import { PaymentMethods } from "@/components/PaymentMethods";

const ease = [0.22, 1, 0.36, 1];
const reveal = (i = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.8, delay: i * 0.08, ease },
});

const PILLARS = [
  [ClipboardList, "Programma", "Allenamento, nutrizione e recupero costruiti su di te, sui tuoi obiettivi e sul tempo che hai davvero a disposizione: sai sempre cosa fare anche nei giorni tra un appuntamento e l'altro."],
  [LineChart, "Monitoraggio", "Si osserva, si misura, si corregge: ogni settimana i progressi vengono controllati e il programma si adatta ai dati, non alle sensazioni.", ["burke2011"]],
  [MessageCircle, "Supporto", "Il trainer HHL ti accompagna per tutti i 90 giorni anche sulle competenze che decidono se un percorso dura: identità, organizzazione e gestione delle emozioni."],
];

const WEEK = [
  [Users, "1 incontro di gruppo", "Ogni settimana ci si ritrova online, per 120 minuti, con il trainer e gli altri partecipanti del percorso. È il momento per fare il punto, portare dubbi e difficoltà, e allenare insieme le competenze del Lifestyle Training: organizzare la settimana, gestire stress, fame nervosa e ricadute. Confrontarsi con chi sta facendo la stessa strada aiuta a non mollare."],
  [Dumbbell, "1 allenamento online", "Ogni settimana, ognuno ha il suo allenamento online seguito dal trainer. Si lavora sulla tecnica, si corregge l'esecuzione e si aggiorna il programma in base ai progressi, così anche gli allenamenti che fai da solo/a durante la settimana sono fatti bene."],
];

const PHASES = [
  ["Giorni 1–30", "Fondamenta", "Si parte dai dati: punto di partenza, obiettivo e tempo che hai davvero a disposizione. Costruiamo il programma di allenamento e nutrizione e diamo un posto fisso nella settimana ad allenamenti, pasti e recupero, così la costanza non dipende dalla forza di volontà."],
  ["Giorni 31–60", "Costanza", "Il programma si adatta ai progressi misurati, non alle sensazioni. In questa fase si allenano le competenze che ti fanno continuare: organizzare la settimana, riconoscere fame nervosa, stress e automatismi, gestire le settimane storte senza mollare tutto."],
  ["Giorni 61–90", "Autonomia", "Le nuove scelte diventano abitudini e parte di chi sei. L'obiettivo è arrivare al novantesimo giorno con risultati misurabili e, soprattutto, con un sistema che sai gestire anche da solo/a, dopo il percorso."],
];

const STEPS = [
  ["01", "Fai il primo passo", "Compili una breve anamnesi: dati, obiettivo, salute, allenamento, stile di vita, abitudini e disponibilità a farti seguire. Bastano pochi minuti."],
  ["02", "Ti ricontatto", "Leggo le tue risposte e ti scrivo per capire insieme se ci sono i presupposti per lavorare insieme. I posti per ogni percorso sono limitati: le richieste vengono valutate in ordine di arrivo."],
  ["03", "Si parte", "Se il percorso fa per te, costruiamo il tuo programma e iniziano i tuoi 90 giorni."],
];

const CheckupButton = ({ testid }) => (
  <Link to="/training-online/primo-passo" data-testid={testid}
    className="group inline-flex items-center gap-3 bg-[#98bfa2] px-7 py-4 font-display text-lg font-800 uppercase text-[#050807] transition-colors hover:bg-[#c4dcca]">
    Fai il primo passo <ArrowUpRight size={18} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
  </Link>
);

const SeatsBadge = ({ s }) => (
  <p data-testid="coaching-seats-left" className="inline-flex items-center gap-2 border border-[#98bfa2]/40 px-4 py-2 font-mono text-xs uppercase tracking-widest text-[#c4dcca]">
    <span className={`h-2 w-2 rounded-full ${s.seats_left > 0 && s.enrollment_open ? "bg-[#98bfa2]" : "bg-[#f0a78f]"}`} />
    {!s.enrollment_open ? "Iscrizioni chiuse · lista d'attesa" : s.seats_left > 0 ? `${s.seats_left} posti liberi su ${s.coaching_seats}` : "Posti esauriti · lista d'attesa"}
  </p>
);

const Hero = ({ s }) => (
  <section className="px-5 pb-20 pt-28 md:px-10 md:pb-28 md:pt-36">
    <div className="mx-auto grid max-w-[1400px] gap-10 lg:grid-cols-[1.3fr_1fr] lg:items-end">
      <div>
        <p className="eyebrow mb-6 flex items-center gap-2"><Laptop size={14} /> Hybrid Health Lab · A distanza · 90 giorni · Max {s.coaching_seats} persone</p>
        <h1 className="font-display text-6xl font-900 uppercase leading-[0.85] md:text-8xl lg:text-9xl">
          Training<br /><span className="text-[#c4dcca]">online.</span>
        </h1>
      </div>
      <div className="space-y-8">
        <p data-testid="coaching-intro" className="text-base leading-relaxed text-[#a3b1aa] md:text-lg">
          {s.coaching_intro || (<>Un percorso di Lifestyle Training di <span className="text-[#f4f7f5]">90 giorni</span>, seguito a distanza dal trainer HHL ovunque tu sia, in un piccolo gruppo <span className="text-[#f4f7f5]">a numero chiuso di {s.coaching_seats} persone</span>. Ogni settimana hai un incontro di gruppo e un allenamento online, più il tuo programma di allenamento, nutrizione e recupero da seguire negli altri giorni.</>)}
        </p>
        <SeatsBadge s={s} />
        <CheckupButton testid="coaching-hero-checkup-btn" />
      </div>
    </div>
  </section>
);

const Week = ({ s }) => (
  <section className="border-t border-[rgba(152,191,162,0.15)] px-5 py-20 md:px-10 md:py-28">
    <div className="mx-auto max-w-[1400px]">
      <motion.p {...reveal()} className="eyebrow mb-4">La tua settimana</motion.p>
      <motion.h2 {...reveal(1)} className="mb-12 max-w-4xl font-display text-5xl font-900 uppercase leading-[0.9] md:text-7xl">
        Due appuntamenti fissi, <span className="text-[#c4dcca]">ogni settimana.</span>
      </motion.h2>
      <div className="grid gap-4 lg:grid-cols-[1fr_1fr_0.8fr]">
        {WEEK.map(([Icon, title, text], i) => (
          <motion.div key={title} {...reveal(i)} data-testid={`coaching-week-${i}`} className="border border-[rgba(152,191,162,0.2)] bg-[#0b120e] p-7 md:p-9">
            <Icon size={24} className="text-[#c4dcca]" />
            <h3 className="mt-10 font-display text-4xl font-900 uppercase leading-none">{title}</h3>
            <p className="mt-4 text-sm leading-relaxed text-[#a3b1aa] md:text-base">{text}</p>
          </motion.div>
        ))}
        <motion.div {...reveal(2)} data-testid="coaching-seats" className="flex flex-col justify-between bg-[#98bfa2] p-7 text-[#050807] md:p-9">
          <p className="font-mono text-xs uppercase tracking-widest">Numero chiuso</p>
          <div className="my-8">
            <p className="font-display text-8xl font-900 leading-none md:text-9xl">{s.coaching_seats}</p>
            <p className="font-display text-2xl font-900 uppercase leading-none">posti per percorso</p>
          </div>
          <p className="text-sm leading-relaxed text-[#050807]/80">Un gruppo piccolo per seguire davvero ogni persona: il trainer conosce la tua storia, i tuoi obiettivi e i tuoi progressi.</p>
        </motion.div>
      </div>
      <motion.div {...reveal(3)} data-testid="coaching-app" className="mt-4 flex flex-col gap-6 border border-[#98bfa2] bg-[#0b120e] p-7 md:flex-row md:items-center md:gap-10 md:p-9">
        <Smartphone size={32} className="shrink-0 text-[#c4dcca]" aria-hidden="true" />
        <div>
          <h3 className="font-display text-4xl font-900 uppercase leading-none">App <span className="text-[#c4dcca]">dedicata.</span></h3>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-[#a3b1aa] md:text-base">Il percorso ti segue anche tra un appuntamento e l'altro: nell'app trovi la tua scheda di allenamento e tracci i tuoi progressi, settimana dopo settimana.</p>
        </div>
      </motion.div>
    </div>
  </section>
);

const Pillars = () => (
  <section className="border-t border-[rgba(152,191,162,0.15)] px-5 py-20 md:px-10 md:py-28">
    <div className="mx-auto max-w-[1400px]">
      <motion.p {...reveal()} className="eyebrow mb-4">Cosa comprende</motion.p>
      <div className="grid gap-4 md:grid-cols-3">
        {PILLARS.map(([Icon, title, text, refs], i) => (
          <motion.div key={title} {...reveal(i)} data-testid={`coaching-pillar-${i}`} className="border border-[rgba(152,191,162,0.2)] bg-[#0b120e] p-7">
            <Icon size={22} className="text-[#c4dcca]" />
            <h3 className="mt-10 font-display text-3xl font-900 uppercase leading-none">{title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-[#a3b1aa]">{text}<Cite refs={refs} /></p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const Journey = () => (
  <section className="border-t border-[rgba(152,191,162,0.15)] px-5 py-20 md:px-10 md:py-28">
    <div className="mx-auto max-w-[1400px]">
      <div className="mb-14 grid gap-8 lg:grid-cols-[1fr_1fr] lg:items-end">
        <motion.h2 {...reveal()} className="font-display text-5xl font-900 uppercase leading-[0.9] md:text-7xl">
          Perché <span className="text-[#c4dcca]">90 giorni.</span>
        </motion.h2>
        <motion.div {...reveal(1)} data-testid="coaching-90-intro" className="space-y-4 text-sm leading-relaxed text-[#a3b1aa] md:text-base">
          <p>Novanta giorni sono il tempo per vedere cambiamenti concreti nella composizione corporea e, soprattutto, per trasformare nuove scelte in abitudini: la ricerca indica che automatizzare un nuovo comportamento richiede in media 66 giorni, con grandi differenze da persona a persona.<Cite refs={["lally2010"]} /> Il principale predittore dei risultati a lungo termine non è la scheda perfetta, ma la capacità di seguirla nel tempo.<Cite refs={["teixeira2012"]} /></p>
          <p>Non è una sfida da superare stringendo i denti: è un periodo per costruire un sistema che continui a funzionare anche dopo, tra lavoro, famiglia, impegni e settimane storte.</p>
          <p data-testid="coaching-habits-note">Cambiare abitudini non significa compilare esercizi vuoti, come fosse uno schedario. Sono esercizi che, affrontati con il giusto atteggiamento, <span className="text-[#f4f7f5]">danno forma a una nuova dimensione personale, più in linea con la tua natura.</span></p>
        </motion.div>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        {PHASES.map(([days, title, text], i) => (
          <motion.div key={title} {...reveal(i)} data-testid={`coaching-phase-${i}`} className="relative border border-[rgba(152,191,162,0.2)] p-7">
            <p className="font-mono text-xs uppercase tracking-widest text-[#98bfa2]">{days}</p>
            <h3 className="mt-6 font-display text-4xl font-900 uppercase leading-none">{title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-[#a3b1aa]">{text}</p>
            <div className="mt-8 h-1 bg-white/10"><div className="h-full bg-[#98bfa2]" style={{ width: `${((i + 1) / 3) * 100}%` }} /></div>
          </motion.div>
        ))}
      </div>
      <motion.div {...reveal(3)} data-testid="coaching-follow-up" className="mt-4 grid gap-6 border border-[#98bfa2] bg-[#0b120e] p-7 md:grid-cols-[auto_1fr] md:items-center md:gap-10 md:p-9">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-[#98bfa2]">Dopo i 90 giorni</p>
          <h3 className="mt-3 font-display text-4xl font-900 uppercase leading-none">Follow up <span className="text-[#c4dcca]">nei mesi successivi.</span></h3>
        </div>
        <p className="text-sm leading-relaxed text-[#a3b1aa] md:text-base">Il percorso non finisce di colpo: <span className="text-[#f4f7f5]">il gruppo si riunisce ogni 3 mesi, con una sessione da 120 minuti.</span> Un momento per fare il punto, mantenere i risultati e continuare a crescere insieme.</p>
      </motion.div>
    </div>
  </section>
);

const TAKEAWAYS = [
  ["Un sistema tuo", "Un modo di allenarti, mangiare e recuperare costruito sulla tua vita reale, che sai gestire anche da solo/a quando il percorso finisce."],
  ["Abitudini che restano", "Scelte ripetute per 90 giorni fino a diventare automatiche: non devi più contare solo sulla forza di volontà."],
  ["Le competenze che fanno durare", "Sapere organizzare la settimana, riconoscere stress e fame nervosa, scegliere in base ai tuoi valori anche nelle settimane storte."],
  ["Conoscere il tuo corpo", "Capire cosa fare e perché: come funzionano allenamento, nutrizione e recupero, per non dipendere dalla prossima moda."],
  ["5 guide da tenere", "Il materiale didattico del percorso resta a te, da riprendere ogni volta che ne hai bisogno."],
  ["Un gruppo che continua", "Le persone con cui hai condiviso il percorso e il follow up ogni 3 mesi per fare il punto e continuare a crescere."],
];

const Takeaways = () => (
  <section className="border-t border-[rgba(152,191,162,0.15)] px-5 py-20 md:px-10 md:py-28" data-testid="coaching-takeaways">
    <div className="mx-auto max-w-[1400px]">
      <motion.p {...reveal()} className="eyebrow mb-4">Alla fine dei 90 giorni</motion.p>
      <motion.h2 {...reveal(1)} className="mb-14 max-w-4xl font-display text-5xl font-900 uppercase leading-[0.9] md:text-7xl">
        Cosa ti porti via dal percorso?
      </motion.h2>
      <ol className="grid gap-x-10 md:grid-cols-2">
        {TAKEAWAYS.map(([title, text], i) => (
          <motion.li key={title} {...reveal(i % 2)} data-testid={`coaching-takeaway-${i}`} className="flex gap-5 border-t border-[rgba(152,191,162,0.2)] py-7">
            <span className="font-mono text-xs text-[#98bfa2]">0{i + 1}</span>
            <div>
              <h3 className="font-display text-2xl font-900 uppercase leading-none md:text-3xl">{title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#a3b1aa] md:text-base">{text}</p>
            </div>
          </motion.li>
        ))}
      </ol>
    </div>
  </section>
);

const GUIDES = ["Organizzazione Settimanale", "Identità — Intelligenza Valoriale", "La Triade dell'Eccellenza", "Respira, poi scegli", "Le Credenze — Metodo LENTE"];
const HOURS = [
  ["Incontri di gruppo", "13 × 120 min", "26 ore"],
  ["Allenamenti online", "13 × 60 min", "13 ore"],
];

const Price = () => (
  <section className="border-t border-[rgba(152,191,162,0.15)] px-5 py-20 md:px-10 md:py-28" data-testid="coaching-price-section">
    <div className="mx-auto max-w-[1400px]">
      <motion.p {...reveal()} className="eyebrow mb-4">Il primo passo</motion.p>
      <div className="grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
        <motion.div {...reveal(1)} className="flex flex-col justify-between bg-[#98bfa2] p-8 text-[#050807] md:p-10">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest">Percorso completo · 3 mesi</p>
            <p data-testid="coaching-free-call" className="mt-6 font-display text-6xl font-900 uppercase leading-[0.85] md:text-8xl">Chiamata gratuita</p>
            <p className="mt-4 font-display text-2xl font-900 uppercase leading-none">senza impegno</p>
          </div>
          <div className="mt-10">
            <p className="text-sm leading-relaxed text-[#050807]/80">Compila il check-up iniziale: lo leggo io e ti chiamo per conoscerci, capire i tuoi obiettivi e raccontarti il percorso nel dettaglio, costi compresi.</p>
            <Link to="/training-online/primo-passo" data-testid="coaching-free-call-btn" className="group mt-6 inline-flex items-center gap-2 bg-[#050807] px-6 py-4 font-display text-lg font-800 uppercase text-[#c4dcca] transition-colors hover:bg-[#0b120e]">
              Prenota la chiamata <ArrowUpRight size={18} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </div>
        </motion.div>
        <motion.div {...reveal(2)} className="border border-[rgba(152,191,162,0.2)] bg-[#0b120e] p-8 md:p-10">
          <p className="font-mono text-xs uppercase tracking-widest text-[#98bfa2]">Ore di training · 13 settimane</p>
          <div className="mt-4" data-testid="coaching-hours">
            {HOURS.map(([label, calc, tot]) => (
              <div key={label} className="flex items-baseline justify-between gap-4 border-b border-white/10 py-3">
                <span className="text-sm text-[#f4f7f5] md:text-base">{label} <span className="font-mono text-xs text-[#a3b1aa]">{calc}</span></span>
                <span className="font-display text-2xl font-800">{tot}</span>
              </div>
            ))}
            <div className="flex items-baseline justify-between gap-4 py-3">
              <span className="font-mono text-xs uppercase tracking-widest text-[#c4dcca]">Totale</span>
              <span data-testid="coaching-hours-total" className="font-display text-4xl font-900 text-[#c4dcca]">39 ore</span>
            </div>
          </div>
          <p className="mt-8 font-mono text-xs uppercase tracking-widest text-[#98bfa2]">Materiale didattico incluso · valore 34,50 €</p>
          <ul className="mt-4 space-y-2" data-testid="coaching-guides">
            {GUIDES.map((g) => (
              <li key={g} className="flex items-center gap-3 text-sm text-[#a3b1aa] md:text-base"><BookOpen size={15} className="shrink-0 text-[#c4dcca]" /> {g} <span className="hidden font-mono text-xs sm:inline">· guida PDF</span></li>
            ))}
          </ul>
          <p className="mt-6 text-sm text-[#a3b1aa]">In più: programma personalizzato di allenamento, nutrizione e recupero, app dedicata con la tua scheda di allenamento e il tracciamento dei progressi, e monitoraggio settimanale.</p>
        </motion.div>
      </div>
      <motion.div {...reveal(3)} className="mt-4 grid gap-4 lg:grid-cols-2"><Guarantee testid="coaching-guarantee" /><PaymentMethods testid="coaching-payment-methods" /></motion.div>
    </div>
  </section>
);

const Steps = ({ s }) => (
  <section className="px-5 py-20 md:px-10 md:py-28">
    <div className="mx-auto max-w-[1400px]">
      <motion.h2 {...reveal()} className="mb-12 max-w-4xl font-display text-5xl font-900 uppercase leading-[0.9] md:text-7xl">
        Prima di iniziare, capiamo se <span className="text-[#c4dcca]">fa per te.</span>
      </motion.h2>
      <div className="grid gap-10 md:grid-cols-3">
        {STEPS.map(([n, title, text], i) => (
          <motion.div key={n} {...reveal(i)} data-testid={`coaching-step-${n}`} className="border-t border-[rgba(152,191,162,0.3)] pt-6">
            <span className="font-mono text-xs text-[#98bfa2]">{n}</span>
            <h3 className="mt-3 font-display text-3xl font-900 uppercase leading-none">{title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-[#a3b1aa]">{text}</p>
          </motion.div>
        ))}
      </div>
      <div className="mt-16 flex flex-col gap-6 border border-[rgba(152,191,162,0.25)] bg-[#0b120e] p-8 md:flex-row md:items-center md:justify-between md:p-10">
        <p className="max-w-xl font-display text-2xl font-800 uppercase leading-tight md:text-3xl">Pochi minuti per capire se ci sono i presupposti per lavorare insieme. Solo {s.coaching_seats} posti per percorso.</p>
        <CheckupButton testid="coaching-bottom-checkup-btn" />
      </div>
    </div>
  </section>
);

export default function CoachingOnline() {
  const s = useSiteSettings();
  return (
    <main data-testid="coaching-online-page">
      <Navbar />
      <CartDrawer />
      <Hero s={s} />
      <Week s={s} />
      <Pillars />
      <Journey />
      <Takeaways />
      <Price />
      <Steps s={s} />
      <Footer />
    </main>
  );
}
